/**
 * Portfolio Creation Wizard
 * TASK-210: Multi-step flow to create Manual or API portfolios
 *
 * Steps:
 * 1. Type Selection (Manual vs API)
 * 2. Basic Details (Name, Custodian, Currency)
 * 3. Connection (for API portfolios) - Blink or OpenWealth
 * 4. Confirmation
 */

import React, { useState } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  Link2,
  Info
} from 'lucide-react';
import { portfolioApiService } from '../../services/api/portfolioApiService';
import { toast } from 'sonner';
import BlinkConnectionModal from './BlinkConnectionModal';

interface CreatedPortfolio {
  portfolio_id: string;
  client_id: string;
  name: string;
  status: string;
  dataSource?: string;
  syncResult?: {
    positions_synced: number;
    cash_synced: number;
    transactions_synced: number;
  };
}

interface PortfolioCreationWizardProps {
  clientId: string;
  clientName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (portfolio: CreatedPortfolio) => void;
}

type PortfolioType = 'manual' | 'api';
type ApiProvider = 'blink_form_six' | 'openwealth' | 'pretim';
type Custodian = 'UBS' | 'Credit Suisse' | 'Julius Baer' | 'Pictet' | 'Vontobel' | 'None';

const PortfolioCreationWizard: React.FC<PortfolioCreationWizardProps> = ({
  clientId,
  clientName,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [portfolioType, setPortfolioType] = useState<PortfolioType | null>(null);
  const [formData, setFormData] = useState({
    portfolioName: '',
    custodian: '' as Custodian | '',
    currency: 'CHF' as 'CHF' | 'EUR' | 'USD',
    apiProvider: '' as ApiProvider | '',
    accountNumber: '',
    externalId: ''  // TASK-321: Pretim external portfolio ID
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showBlinkModal, setShowBlinkModal] = useState(false);
  const [createdPortfolio, setCreatedPortfolio] = useState<CreatedPortfolio | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setStep(1);
    setPortfolioType(null);
    setFormData({
      portfolioName: '',
      custodian: '' as Custodian | '',
      currency: 'CHF',
      apiProvider: '' as ApiProvider | '',
      accountNumber: '',
      externalId: ''
    });
    onClose();
  };

  const handleNext = () => {
    if (step === 1 && !portfolioType) {
      toast.error('Please select a portfolio type');
      return;
    }

    if (step === 2) {
      if (!formData.portfolioName.trim()) {
        toast.error('Please enter a portfolio name');
        return;
      }
      if (!formData.custodian) {
        toast.error('Please select a custodian');
        return;
      }
    }

    // TASK-321: Validate external ID for Pretim
    if (step === 3 && portfolioType === 'api' && formData.apiProvider === 'pretim') {
      if (!formData.externalId.trim()) {
        toast.error('Please enter the Pretim Portfolio ID');
        return;
      }
    }

    if (step < 4) {
      // Skip connection step for manual portfolios
      if (step === 2 && portfolioType === 'manual') {
        setStep(4);
      } else {
        setStep((step + 1) as any);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      // Skip connection step when going back from manual portfolio
      if (step === 4 && portfolioType === 'manual') {
        setStep(2);
      } else {
        setStep((step - 1) as any);
      }
    }
  };

  const handleCreate = async () => {
    setIsCreating(true);

    try {
      // Step 1: Create portfolio via real API
      const createResult = await portfolioApiService.createPortfolio(clientId, {
        name: formData.portfolioName,
        portfolio_type: 'investment',  // Valid types: investment, retirement, trust, savings, other
        custodian: formData.custodian || undefined,
        currency: formData.currency,
        is_default: false,
        // TASK-321: Include Pretim external ID and data source
        external_portfolio_id: formData.externalId || undefined,
        data_source: portfolioType === 'api' ? formData.apiProvider : 'manual'
      });

      let syncResult = undefined;
      const dataSource = portfolioType === 'manual' ? 'manual_entry' : formData.apiProvider;

      // Step 2: If Pretim selected, sync data from Pretim API
      if (portfolioType === 'api' && formData.apiProvider === 'pretim') {
        try {
          const pretimResult = await portfolioApiService.syncPretim(
            clientId,
            createResult.portfolio_id,
            formData.externalId  // TASK-321: Pass external portfolio ID
          );
          syncResult = {
            positions_synced: pretimResult.synced.positions_synced,
            cash_synced: pretimResult.synced.cash_synced,
            transactions_synced: pretimResult.synced.transactions_synced
          };
          toast.success(
            `Portfolio created with ${syncResult.positions_synced} positions synced from Pretim!`
          );
        } catch (syncError) {
          console.error('Pretim sync failed:', syncError);
          toast.warning('Portfolio created but Pretim sync failed. You can retry later.');
        }
      } else {
        toast.success(`Portfolio "${formData.portfolioName}" created successfully!`);
      }

      const newPortfolio: CreatedPortfolio = {
        portfolio_id: createResult.portfolio_id,
        client_id: createResult.client_id,
        name: createResult.name,
        status: createResult.status,
        dataSource,
        syncResult
      };

      // If API portfolio with Blink/OpenWealth, show connection modal (coming soon)
      if (portfolioType === 'api' && (formData.apiProvider === 'blink_form_six' || formData.apiProvider === 'openwealth')) {
        setCreatedPortfolio(newPortfolio);
        setShowBlinkModal(true);
      } else {
        onSuccess(newPortfolio);
        handleClose();
      }
    } catch (error) {
      console.error('Failed to create portfolio:', error);
      toast.error('Failed to create portfolio. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleBlinkSuccess = (data: { positionsCount: number; totalValue: number }) => {
    toast.success(`Successfully synced ${data.positionsCount} positions!`);
    setShowBlinkModal(false);
    if (createdPortfolio) {
      onSuccess(createdPortfolio);
    }
    handleClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Portfolio</h2>
            <p className="text-sm text-gray-500 mt-1">for {clientName}</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Type' },
              { num: 2, label: 'Details' },
              ...(portfolioType === 'api' ? [{ num: 3, label: 'Connect' }] : []),
              { num: 4, label: 'Confirm' }
            ].map((s, index) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s.num
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="text-xs text-gray-600 mt-1">{s.label}</span>
                </div>
                {index < (portfolioType === 'api' ? 3 : 2) && (
                  <div className="flex-1 h-0.5 bg-gray-200 mx-2" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* Step 1: Type Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Select Portfolio Type
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Manual Portfolio */}
                <button
                  onClick={() => setPortfolioType('manual')}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    portfolioType === 'manual'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                    <Upload className="w-6 h-6 text-gray-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    📊 Manual Portfolio
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload files or add positions manually
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Securities via Excel/CSV</li>
                    <li>• Alternative assets with documents</li>
                    <li>• Manual entries and updates</li>
                  </ul>
                </button>

                {/* API Portfolio */}
                <button
                  onClick={() => setPortfolioType('api')}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    portfolioType === 'api'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Link2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    🔌 API Portfolio
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Auto-sync from custodian bank
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Blink Form Six</li>
                    <li>• OpenWealth API</li>
                    <li>• Daily auto-updates</li>
                  </ul>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Basic Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Portfolio Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portfolio Name *
                </label>
                <input
                  type="text"
                  value={formData.portfolioName}
                  onChange={(e) => setFormData({ ...formData, portfolioName: e.target.value })}
                  placeholder="e.g., UBS Main Account, Private Assets"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custodian *
                </label>
                <select
                  value={formData.custodian}
                  onChange={(e) => setFormData({ ...formData, custodian: e.target.value as Custodian })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Custodian</option>
                  <option value="UBS">UBS</option>
                  <option value="Credit Suisse">Credit Suisse</option>
                  <option value="Julius Baer">Julius Baer</option>
                  <option value="Pictet">Pictet</option>
                  <option value="Vontobel">Vontobel</option>
                  <option value="None">None (Self-managed)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Currency *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="CHF">CHF</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              {portfolioType === 'api' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                    placeholder="e.g., CH-****1234"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 3: API Connection (only for API portfolios) */}
          {step === 3 && portfolioType === 'api' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Choose Data Source
              </h3>

              <div className="grid grid-cols-3 gap-4">
                {/* Pretim - Available */}
                <button
                  onClick={() => setFormData({ ...formData, apiProvider: 'pretim' })}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    formData.apiProvider === 'pretim'
                      ? 'border-green-600 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Pretim
                    </h4>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Available
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Portfolio management system
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>✓ Positions & transactions</li>
                    <li>✓ Cash accounts</li>
                    <li>✓ Test data ready</li>
                  </ul>
                </button>

                {/* Blink Form Six - Coming Soon */}
                <button
                  onClick={() => setFormData({ ...formData, apiProvider: 'blink_form_six' })}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    formData.apiProvider === 'blink_form_six'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Blink Form Six
                    </h4>
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                      Coming Soon
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Swiss banking standard
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>○ Bank-grade encryption</li>
                    <li>○ OAuth 2.0 authentication</li>
                    <li>○ Real-time sync</li>
                  </ul>
                </button>

                {/* OpenWealth - Coming Soon */}
                <button
                  onClick={() => setFormData({ ...formData, apiProvider: 'openwealth' })}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${
                    formData.apiProvider === 'openwealth'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900">
                      OpenWealth
                    </h4>
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                      Coming Soon
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Industry-standard API
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>○ Standardized data format</li>
                    <li>○ API key authentication</li>
                    <li>○ Scheduled sync</li>
                  </ul>
                </button>
              </div>

              {formData.apiProvider === 'pretim' && (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
                    <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-green-900">
                      <p className="font-medium mb-1">Pretim Integration Ready</p>
                      <p className="text-green-700">
                        Your portfolio will be populated with positions, cash accounts, and transactions from Pretim.
                      </p>
                    </div>
                  </div>

                  {/* TASK-321: Pretim External Portfolio ID Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pretim Portfolio ID *
                    </label>
                    <input
                      type="text"
                      value={formData.externalId}
                      onChange={(e) => setFormData({ ...formData, externalId: e.target.value })}
                      placeholder="e.g., 7b9a7fe2-503a-4cee-95cd-08dc4cbe2da2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Enter the portfolio ID from Pretim to sync positions and transactions.
                    </p>
                  </div>
                </div>
              )}

              {(formData.apiProvider === 'blink_form_six' || formData.apiProvider === 'openwealth') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-medium mb-1">Integration Coming Soon</p>
                    <p className="text-amber-700">
                      {formData.apiProvider === 'blink_form_six' ? 'Blink Form Six' : 'OpenWealth'} integration is under development.
                      For now, you can create the portfolio and connect later.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Portfolio Creation
              </h3>

              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Portfolio Name</div>
                  <div className="font-medium text-gray-900">{formData.portfolioName}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Type</div>
                    <div className="font-medium text-gray-900">
                      {portfolioType === 'manual' ? '📊 Manual' : '🔌 API'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Custodian</div>
                    <div className="font-medium text-gray-900">{formData.custodian}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Base Currency</div>
                    <div className="font-medium text-gray-900">{formData.currency}</div>
                  </div>
                  {portfolioType === 'api' && formData.apiProvider && (
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Data Source</div>
                      <div className="font-medium text-gray-900">
                        {formData.apiProvider === 'pretim' ? 'Pretim' :
                         formData.apiProvider === 'blink_form_six' ? 'Blink Form Six' : 'OpenWealth'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {portfolioType === 'manual' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    After creation, you can upload Excel/CSV files or add alternative assets to this portfolio.
                  </p>
                </div>
              )}

              {portfolioType === 'api' && formData.apiProvider === 'pretim' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-900">
                    Positions, cash accounts, and transactions will be synced from Pretim automatically.
                  </p>
                </div>
              )}

              {portfolioType === 'api' && formData.apiProvider !== 'pretim' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-900">
                    {formData.apiProvider === 'blink_form_six' ? 'Blink Form Six' : 'OpenWealth'} integration coming soon.
                    Portfolio will be created without data sync.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={step === 1 ? handleClose : handleBack}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {step === 1 ? 'Cancel' : (
              <>
                <ChevronLeft className="w-4 h-4 inline mr-1" />
                Back
              </>
            )}
          </button>

          <button
            onClick={step === 4 ? handleCreate : handleNext}
            disabled={isCreating || (step === 3 && !formData.apiProvider)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isCreating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : step === 4 ? (
              <>
                <Check className="w-4 h-4" />
                Create Portfolio
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Blink Connection Modal (triggered after Blink/OpenWealth portfolio creation) */}
      {createdPortfolio && showBlinkModal && (
        <BlinkConnectionModal
          isOpen={showBlinkModal}
          custodian={formData.custodian as any}
          portfolioId={createdPortfolio.portfolio_id}
          onSuccess={handleBlinkSuccess}
          onClose={() => {
            setShowBlinkModal(false);
            if (createdPortfolio) {
              onSuccess(createdPortfolio);
            }
            handleClose();
          }}
        />
      )}
    </div>
  );
};

export default PortfolioCreationWizard;
